import { useState } from "react";
import { KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { Button } from "./ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "./ui/alert-dialog";

export const ResetPasswordButton = ({ endpoint, label, onDone, testId }) => {
  const [busy, setBusy] = useState(false);

  const reset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(
        `${endpoint}?origin_url=${encodeURIComponent(window.location.origin)}`
      );
      toast.success(
        data.sent
          ? `Reset email sent to ${data.email}. Backup password: ${data.temp_password}`
          : `New password for ${data.email}: ${data.temp_password} (email failed: ${data.error})`,
        { duration: 10000 }
      );
      await onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          data-testid={`${testId}-open`}
          className="rounded-full border-slate-300 transition-colors duration-200 hover:bg-slate-100"
        >
          <KeyRound className="mr-1.5 h-3.5 w-3.5" /> Reset password
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="font-display text-xl font-extrabold tracking-tight">
            Issue a new password?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This invalidates the old password for {label} immediately and emails them a link to
            choose a new one. A backup password is also shown below their login.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel className="rounded-full" data-testid={`${testId}-cancel`}>
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={reset}
            disabled={busy}
            data-testid={`${testId}-confirm`}
            className="rounded-full bg-blue-600 font-semibold hover:bg-blue-700"
          >
            {busy ? "Resetting…" : "Generate new password"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
